// Hook for polling a DiscoveryRun's status + event log.
//
// Used by DiscoveryPanel inside the jetson card. Polls at 2s while the
// run is non-terminal (created / running / paused_on_creds), stops on
// terminal status. Exposes typed action callbacks (cancel/resume/skip)
// for the panel controls + credential-prompt UI.
//
// SWR is used purely as a fetcher with poll interval — we don't need
// its mutation/optimistic-update machinery here.

import useSWR from 'swr';
import { useCallback } from 'react';
import {
  cancelDiscoveryRun,
  getDiscoveryRun,
  isLive,
  isTerminal,
  resumeDiscoveryRun,
  type DiscoveryRunDetail,
  type DiscoveryRunStatus,
  type ResumeAction,
} from '@/api/discoveryRuns';
import type { Credentials, DiscoveryEvent } from '@/api/discovery';

const POLL_INTERVAL_MS = 2000;

export interface UseDiscoveryRunOpts {
  /**
   * Disable the hook. Useful for DiscoveryPanel's "collapsed but not
   * cancelled" state — we keep the runId in URL hash but stop polling
   * to save server load while the user is on another tab.
   */
  paused?: boolean;
}

export interface UseDiscoveryRunReturn {
  /** Latest run state — undefined while initial fetch is in flight. */
  run: DiscoveryRunDetail | undefined;
  /** Convenience: latest events. Empty array while loading. */
  events: DiscoveryEvent[];
  /** Convenience: current status. 'created' while loading. */
  status: DiscoveryRunStatus;
  /** True while the run is non-terminal AND the hook is polling. */
  isLive: boolean;
  /** True once the run reached a terminal status. */
  isTerminal: boolean;
  /** SWR loading flag (true only during the very first fetch). */
  isLoading: boolean;
  /** SWR error if the most recent poll failed (network etc.). */
  error: unknown;
  /** Manually refetch outside the polling cadence. */
  refresh: () => Promise<DiscoveryRunDetail | undefined>;
  /** Request cancel. Already-persisted devices stay. */
  cancel: () => Promise<void>;
  /** Resume from a credential_needed pause with user-typed candidates. */
  submitCredentials: (candidates: Credentials[]) => Promise<void>;
  /** Resume from a credential_needed pause by skipping the device. */
  skipDevice: () => Promise<void>;
}

export function useDiscoveryRun(
  runId: string | null | undefined,
  opts: UseDiscoveryRunOpts = {},
): UseDiscoveryRunReturn {
  const enabled = !!runId && !opts.paused;
  const swrKey = enabled ? `discovery-run:${runId}` : null;

  const { data, error, isLoading, mutate } = useSWR<DiscoveryRunDetail>(
    swrKey,
    () => getDiscoveryRun(runId!).then(r => r.data),
    {
      // Stop the interval once we hit a terminal status — SWR's
      // refreshInterval can return 0 to disable polling for the next
      // tick. This naturally winds down the loop without a separate
      // useEffect dance.
      refreshInterval: latest =>
        latest && isTerminal(latest.status) ? 0 : POLL_INTERVAL_MS,
      revalidateOnFocus: true,
      shouldRetryOnError: false,
    },
  );

  const refresh = useCallback(async () => mutate(), [mutate]);

  const cancel = useCallback(async () => {
    if (!runId) return;
    await cancelDiscoveryRun(runId);
    await mutate();
  }, [runId, mutate]);

  const submit = useCallback(
    async (action: ResumeAction) => {
      if (!runId) return;
      await resumeDiscoveryRun(runId, action);
      await mutate();
    },
    [runId, mutate],
  );

  const submitCredentials = useCallback(
    (candidates: Credentials[]) => submit({ action: 'try', candidates }),
    [submit],
  );
  const skipDevice = useCallback(() => submit({ action: 'skip' }), [submit]);

  const status: DiscoveryRunStatus = data?.status ?? 'created';
  return {
    run: data,
    events: data?.events ?? [],
    status,
    isLive: enabled && isLive(status),
    isTerminal: isTerminal(status),
    isLoading,
    error,
    refresh,
    cancel,
    submitCredentials,
    skipDevice,
  };
}
